import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteView from './DeleteView';
import GenerationLimitModal from './GenerationLimitModal';
import { retrieveViews } from '../services/database';
import { canUserGenerate } from '../services/subscriptionCheck';
import { useServiceStatus } from '../context/ServiceStatusContext';
import { Trash2 } from 'lucide-react';

const Views = ({ projectId }) => {
  const navigate = useNavigate();
  const [views, setViews] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // <-- Added loading state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('trial');
  const { serviceDown } = useServiceStatus();

  useEffect(() => {
    const fetchViews = async () => {
      try {
        const viewsData = await retrieveViews(projectId);
        setViews(viewsData);
      } catch (error) {
        console.error('Error retrieving views:', error);
      } finally {
        setIsLoading(false); // <-- Set loading to false after data is fetched
      }
    };
    fetchViews();
  }, [projectId]);

  const handleNewView = async () => {
    // If service is down, don't proceed
    if (serviceDown) return;
    
    setIsCheckingSubscription(true);
    try {
      // Check if user can create a generation
      const { canGenerate, subscriptionStatus: status } = await canUserGenerate();
      
      // Save the subscription status for the modal
      setSubscriptionStatus(status);
      
      if (canGenerate) {
        navigate('/create-view', { state: { projectId } });
      } else {
        // Show limit modal if they can't generate
        setShowLimitModal(true);
      }
    } catch (error) {
      console.error('Error checking generation permission:', error);
      navigate('/create-view', { state: { projectId } });
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  const handleViewClick = (viewId) => {
    navigate('/view-details', { state: { projectId, viewId } });
  };

  const handleDeleteClick = (viewId) => {
    setSelectedViewId(viewId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedViewId(null);
  };

  const closeLimitModal = () => {
    setShowLimitModal(false);
  };

  const deleteView = (viewId) => {
    setViews(views.filter((view) => view._id !== viewId));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Views</h2>
        <button
          onClick={handleNewView}
          disabled={isCheckingSubscription || serviceDown}
          className={`text-white bg-black px-4 py-2 rounded-md hover:bg-gray-800 transition-colors duration-200 ${
            isCheckingSubscription || serviceDown ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          title={serviceDown ? "Service is currently down" : "Create a new view"}
        >
          {isCheckingSubscription ? 'Checking...' : '+ New'}
        </button>
      </div>
      <hr className="mb-4 border-gray-300" />
      {isLoading ? (
        <p>Loading views...</p>  // <-- Show loading state
      ) : views.length === 0 ? (
        <p>No views created yet.</p>
      ) : (
        <ul>
          {views.map((view) => (
            <li
              key={view._id}
              className="flex items-center justify-between border p-2 rounded-md mb-2 hover:border-black"
            >
              <button
                onClick={() => handleViewClick(view._id)}
                className="text-left flex-grow"
              >
                {view.title.length > 30 ? view.title.substring(0, 30) + '...' : view.title}
              </button>
              <button
                onClick={() => handleDeleteClick(view._id)}
                className="ml-2 text-gray-400 hover:text-red-600"
              >
                <Trash2 size={20} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {showDeleteModal && (
        <DeleteView
          viewId={selectedViewId}
          onClose={closeDeleteModal}
          onDelete={deleteView}
        />
      )}
      {showLimitModal && (
        <GenerationLimitModal 
          onClose={closeLimitModal} 
          subscriptionStatus={subscriptionStatus}
          isExpired={subscriptionStatus === "expired"}
        />
      )}
    </div>
  );
};

export default Views;
